import { useEffect, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { FactTile, Ring } from '../ui/bits.tsx';
import { Icon } from '../ui/Icon.tsx';
import { asset } from '../engine/assets.ts';
import { haptic, hapticDone } from '../engine/haptics.ts';
import { CEL, CORAL_DEEP, CREAM, GRAD, INK, LINE, MUTED, ORANGE, PAPER, PROGRESS_FILL, SUN, VIOLET, SHADOW_FLOAT } from '../ui/theme.ts';

/**
 * variants numbers | profile | plan. Rows complete on `stepMs`, the fact tile
 * rotates every 3 ticks, and the step auto-advances 150 ms after the last row.
 * Not skippable: there is no CTA and no back target while it runs.
 */
export function Loader({ step, content, ctx }: StepProps) {
  const rows = content.rows ?? [];
  const facts = ctx.shared.facts ?? [];
  const stepMs = step.stepMs ?? 2000;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    ctx.setCta({ label: null });
    const id = setInterval(() => setTick((t) => t + 1), stepMs);
    return () => clearInterval(id);
  }, [step.id, stepMs]);

  const done = Math.min(rows.length, tick);

  useEffect(() => {
    if (done === 0) return;
    if (done >= rows.length) hapticDone();
    else haptic(10);
  }, [done, rows.length]);

  useEffect(() => {
    if (tick <= rows.length) return;
    const t = setTimeout(() => (step.autoNext ? ctx.goTo(step.autoNext) : ctx.next()), 150);
    return () => clearTimeout(t);
  }, [tick, rows.length, step.autoNext]);

  const pct = rows.length ? Math.round((done / rows.length) * 100) : 0;
  const ui = ctx.shared.ui ?? {};
  const eta = done >= rows.length ? (ui.done ?? 'Done') : (ui.secondsLeft ?? '{n}s left').replace('{n}', String(Math.max(1, Math.ceil((rows.length - done) * (stepMs / 1000)))));
  const fact = facts.length ? facts[Math.floor(tick / 3) % facts.length] : '';

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
        {step.variant === 'plan' ? <PlanCard content={content} ui={ui} done={done} total={rows.length} /> : null}

        {step.variant === 'profile' ? (
          <span
            style={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              background: CEL,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: `0 14px 36px ${VIOLET}4d`,
              animation: 'fk-pulse 1.4s ease-out infinite',
            }}
          >
            <Icon name="user-focus" size={44} />
          </span>
        ) : null}

        {step.variant === 'numbers' ? <Ring /> : null}

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 800, color: MUTED }}>
            <span>{pct}%</span>
            <span>{eta}</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: CREAM, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 4, background: PROGRESS_FILL, width: `${Math.max(4, pct)}%`, transition: 'width .6s cubic-bezier(.2,.8,.2,1)' }} />
          </div>
        </div>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }} aria-live="polite">
          {rows.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                opacity: i <= done ? 1 : 0.4,
                padding: '10px 12px',
                borderRadius: 16,
                background: i === done ? CREAM : 'transparent',
                transition: 'all .3s',
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  flex: 'none',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: i < done ? CEL : PAPER,
                  border: `2px solid ${i < done ? 'transparent' : i === done ? ORANGE : LINE}`,
                  color: '#fff',
                  animation: i === done && i < rows.length ? 'fk-pulse 1s ease-out infinite' : 'none',
                  marginTop: 1,
                }}
              >
                <i className="ph-bold ph-check" style={{ fontSize: 13, opacity: i < done ? 1 : 0 }} aria-hidden="true" />
              </span>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <div style={{ fontWeight: i <= done ? 800 : 600 }}>{row.label}</div>
                {i < done && row.detail ? (
                  <div style={{ fontSize: 13, color: MUTED, fontWeight: 600, animation: 'fk-in .3s ease-out' }}>{row.detail}</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {/* the plan variant already has the assembling card; the fact tile on
            top of it pushed the last rows below the fold on a phone */}
        {fact && step.variant !== 'plan' ? <FactTile text={fact} /> : null}
      </div>
    </>
  );
}

/** the "plan" variant's assembling card: chips → curve → moves, with a scan line until done. */
function PlanCard({ content, ui, done, total }: { content: StepProps['content']; ui: NonNullable<StepProps['ctx']['shared']['ui']>; done: number; total: number }) {
  const chips = content.plan?.chips ?? [];
  const moves = content.plan?.moves ?? [];
  const chipStyles = [
    { bg: GRAD, fg: '#fff' },
    { bg: INK, fg: '#fff' },
    { bg: PAPER, fg: INK },
  ];
  return (
    <div
      style={{
        flex: 'none',
        position: 'relative',
        width: '100%',
        borderRadius: 26,
        overflow: 'hidden',
        boxShadow: SHADOW_FLOAT,
        background: PAPER,
        border: `2px solid ${LINE}`,
        padding: 14,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 10,
        textAlign: 'left',
      }}
    >
      <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <img src={asset('boostsy-icon')} alt="" width={18} height={18} style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: CORAL_DEEP }}>{ui.yourPlan ?? 'Your plan'}</span>
        </span>
        <span style={{ fontSize: 11, fontWeight: 800, color: MUTED }}>{ui.ninetyDays ?? '90 days'}</span>
      </div>

      <div style={{ gridColumn: '1 / -1', padding: 10, borderRadius: 14, background: CREAM }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>{ui.aboutYou ?? 'About you'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {chips.map((c, i) => (
            <span
              key={c}
              style={{
                padding: '4px 9px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 800,
                background: chipStyles[i % 3].bg,
                color: chipStyles[i % 3].fg,
                opacity: done >= 1 ? 1 : 0.25,
                transition: 'all .4s',
              }}
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      <div style={{ padding: 10, borderRadius: 14, background: SUN, display: 'flex', flexDirection: 'column', minHeight: 96 }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: INK, opacity: 0.7 }}>{ui.projectedReach ?? 'Projected reach'}</div>
        <svg viewBox="0 0 140 60" width="100%" height="56" style={{ display: 'block', marginTop: 'auto' }} aria-hidden="true">
          <path
            d="M0 54 C 30 52, 55 46, 80 34 S 120 12, 140 5"
            fill="none"
            stroke="#fff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="200"
            strokeDashoffset={done >= 2 ? 0 : 200}
            style={{ transition: 'stroke-dashoffset .8s ease-out' }}
          />
          <circle cx="140" cy="5" r="4" fill={INK} stroke="#fff" strokeWidth="2" opacity={done >= 2 ? 1 : 0} />
        </svg>
      </div>

      <div style={{ padding: 10, borderRadius: 14, background: CEL, color: '#fff', minHeight: 96 }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>{ui.firstMoves ?? 'First 3 moves'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {moves.map((m) => (
            <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, opacity: done >= 3 ? 1 : 0.2, transition: 'opacity .4s' }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: VIOLET, flex: 'none' }}>
                <i className="ph-bold ph-check" style={{ fontSize: 9 }} aria-hidden="true" />
              </span>
              {m}
            </div>
          ))}
        </div>
      </div>

      {done < total ? (
        <div
          style={{ position: 'absolute', left: 0, right: 0, height: 3, background: '#fff', boxShadow: '0 0 14px 5px rgba(255,255,255,.9)', animation: 'fk-scan 2.2s ease-in-out infinite alternate' }}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}
